import {Component} from 'react';
import h from 'react-hyperscript';
import './ReportCardOverview.scss';
import Hero from 'Pages/ReportCardOverview/Hero/Hero';
import Archive from 'Pages/ReportCardOverview/Archive/Archive';
// eslint-disable-next-line max-len
import SurveyReportStatus from 'Pages/ReportCardOverview/SurveyReportStatus/SurveyReportStatus';
import content from 'Assets/data/report-card-overview.json';
import {ENDPOINTS, getAccessToken, fetchTimeout} from 'Src/utils/utils';
import heroImage from 'Assets/report-hero.svg';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import ErrorPage from 'Components/ErrorPage/ErrorPage';

class ReportCardOverview extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;

    const isBeforeAvailable = this.isBeforeReportAvailableDate(
        this.props.userStatus.dates.report_available_date
    );

    this.state = {
      surveyStatus: {
        share_survey_url: '',
      },
      newReportAvailable: !isBeforeAvailable,
      surveyOpenPeriod: this.props.userStatus.survey_open_period_flag,
      latestSurveyComplete: this.props.userStatus.user_survey_state,
      nextSurveyDate: this.props.userStatus.dates.next_survey_date,
      activeComponent: 'ReportAvailableState',
      reportList: null,
      sharedUsers: [],
      latestReport: null,
      isLoaded: false,
      reportMissingDataNotFound: false,
      errorPage: undefined,
      districtList: null,
    };
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userStatus !== this.props.userStatus) {
      this.setLatestSurveyComplete();
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this.setLatestSurveyComplete();

    this.setState({
      reportLoading: true,
    });

    if (this.props.userStatus.survey_open_period_flag) {
      fetch(ENDPOINTS.SURVEY_STATUS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.props.idToken,
          'X-CSRF-Token': this.props.csrf,
        },
        body: JSON.stringify({
          access_token: getAccessToken(this.props.googleUser),
        }),
      })
          .then((response) => response.json())
          .then(
              (result) => {
                if (result.data) {
                  if (this._isMounted) {
                    this.setSurveyStatus(result.data);
                  }
                } else {
                  console.error('Result needs attention: ', result);
                }
              },
              (error) => {
                console.error('No result', error);
              }
          );
    }

    this.fetchDistrictsList();
  }

  fetchReportList() {
    return fetchTimeout(ENDPOINTS.REPORT_LIST, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.props.idToken,
        'X-CSRF-Token': this.props.csrf,
      },
      body: JSON.stringify({
        access_token: getAccessToken(this.props.googleUser),
        district_id: this.state.selectedDistrict.id,
      }),
    })
        .then((response) => {
          return response.json();
        })
        .then(
            (result) => {
              if (result.report_cards) {
                if (result.report_cards.length < 1) {
                  this.setReportList(null);
                } else {
                  this.setReportList(result.report_cards);
                  const rcdId = result.report_cards[0].district_report_card_id;
                  window.localStorage.setItem('rcdId', rcdId);
                  this.setState({
                    ...this.state,
                    sharedUsers: result.shared_users,
                  });
                }
              } else {
                console.error('Result needs attention: ', result);
              }
            }
        ).catch((error) => {
          if (error.name === 'AbortError') {
            this.setState({
              errorPage: 'timeout',
              isLoaded: true,
            });
          }
        });
  }

  fetchDistrictsList() {
    return fetch(ENDPOINTS.DISTRICT_LIST, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.props.idToken,
        'X-CSRF-Token': this.props.csrf,
      },
      body: JSON.stringify({
        access_token: getAccessToken(this.props.googleUser),
      }),
    })
        .then((response) => {
          return response.json();
        })
        .then(
            (result) => {
              this.setState({
                districtList: result,
                selectedDistrict: result[0],
              });

              this.fetchReportList();
            },
            (error) => {
              console.log('No result', error);
            }
        );
  }

  async setDistrictFilter(district) {
    await this.setState({
      isLoaded: false,
      selectedDistrict: district,
    });

    this.fetchReportList();
  }

  setSurveyStatus(data) {
    this.setState({
      surveyStatus: data,
    });
  }

  reportMissingData(reportList) {
    return (
      reportList &&
      Array.isArray(reportList) &&
      reportList.some((report) => report.missing_data)
    );
  }

  isBeforeReportAvailableDate(reportAvailableDate) {
    const dateAvailable = new Date(reportAvailableDate);
    const dateNow = new Date();

    return dateNow < dateAvailable;
  }

  setReportList(data) {
    this.setState(
        {
          reportList: data,
        },
        () => {
          this.setState({
            isLoaded: true,
          });
          if (this.state.reportList && this.state.reportList.length > 0) {
            this.props.setLatestReportName(
                this.state.reportList[0].report_card_name);
            this.setLatestReport(this.state.reportList[0]);
          }
        }
    );

    this.setLatestSurveyComplete();
  }

  setLatestReport(value) {
    this.setState({
      latestReport: value,
    });
  }

  setLatestSurveyComplete() {
    if (this._isMounted) {
      let value;

      if (
        this.props.userStatus.user_survey_state === 'Not Started' ||
        this.props.userStatus.user_survey_state === 'Partial'
      ) {
        value = false;
      } else {
        value = true;
      }

      this.setState(
          {
            latestSurveyComplete: value,
          },
          () => {
            this.checkSurveyAndReportStatus();
          }
      );
    }
  }

  /**
   * Below are the different configurations for our Survey/Report states:
   *
   * Report is pending
   * The user has completed the latest survey and the survey open period has
   * ended, so their newest report is available BUT a report has the
   * missing_data true field.
   *
   *  newReportAvailable: true
   *  surveyOpenPeriod: true
   *  latestSurveyComplete: true
   *
   * Report is locked in
   * The survey window is closed, so their newest report is locked in
   * with the data.
   *
   *  newReportAvailable: true
   *  surveyOpenPeriod: false
   *
   * Survey is Available
   * There is a new survey available, but the user has not yet taken it. Since
   * we're in an open period, there is no report.
   *
   *  newReportAvailable: false
   *  surveyOpenPeriod: true
   *  latestSurveyComplete: false
   *
   * Survey is Complete
   * The user has completed the latest survey in our open survey period, but
   * the latest report is not yet available.
   *
   *  newReportAvailable: false
   *  surveyOpenPeriod: true
   *  latestSurveyComplete: true
   *
   * Empty State / No Reports or surveys available
   * No reports are available yet, and we are not in an open survey period
   * so the latest survey is not completed either.
   *
   *  newReportAvailable: false
   *  surveyOpenPeriod: false
   *  latestSurveyComplete: false
   */
  checkSurveyAndReportStatus() {
    const surveyIsClosed = !this.state.surveyOpenPeriod;
    const surveyNotComplete = !this.state.latestSurveyComplete;
    const hasReport = this.state.latestReport;
    const reportLockedIn =
      surveyIsClosed &&
      hasReport;
    const surveyIsAvailable =
      this.state.surveyOpenPeriod && surveyNotComplete && hasReport;
    const surveyIsComplete =
      this.state.latestSurveyComplete && hasReport;

    let activeComponent = 'NoReportState';

    if (reportLockedIn) {
      activeComponent = 'ReportAvailableState';
    } else if (surveyIsAvailable) {
      activeComponent = 'SurveyAvailableState';
    } else if (surveyIsComplete) {
      activeComponent = 'SurveyCompleteState';
    }

    this.setSurveyReportStatus(activeComponent);
  }

  setSurveyReportStatus(value) {
    this.setState({
      activeComponent: value,
    });
  }

  render() {
    if (this.state.isLoaded) {
      const reportMissingDataNotFound = this.state.reportMissingDataNotFound;
      if (this.state.errorPage) {
        return h(ErrorPage, {
          errorType: this.state.errorPage,
        });
      }
      return h(
          'div',
          {
            className: 'container',
          },
          [
            h(
                Hero,
                {
                  key: 'report-card-overview__hero',
                  image: heroImage,
                  content: content.hero.admin,
                },
                null
            ),

            h(
                SurveyReportStatus,
                {
                  key: 'report-card-overview__status',
                  userStatus: this.props.userStatus,
                  googleUser: this.props.googleUser,
                  csrf: this.props.csrf,
                  idToken: this.props.idToken,
                  reportLoading: this.state.reportLoading,
                  surveyStatus: this.state.surveyStatus,
                  activeComponent: this.state.activeComponent,
                  latestReport: this.state.latestReport,
                  nextSurveyDate: this.state.nextSurveyDate,
                  reportMissingDataNotFound: reportMissingDataNotFound,
                  calendarContent: content.cal,
                  calendarDate: this.props.userStatus.survey_open_period_start,
                  reportList: this.state.reportList,
                  setProductWindowsLoading: this.props.setProductWindowsLoading,
                },
                null
            ),

            h(
                Archive,
                {
                  key: 'report-card-overview__archive',
                  districtName: this.props.userStatus.district_name,
                  districtId: this.props.userStatus.district_id,
                  activeComponent: this.state.activeComponent,
                  reportList: this.state.reportList,
                  districtList: this.state.districtList,
                  selectedDistrict: this.state.selectedDistrict,
                  setDistrictFilter:
                      (district) => this.setDistrictFilter(district),
                  sharedUsers: this.state.sharedUsers,
                  setSharedUsers: (users) => this.setState({
                    ...this.state,
                    sharedUsers: users,
                  }),
                  userStatus: this.props.userStatus,
                  googleUser: this.props.googleUser,
                  csrf: this.props.csrf,
                  idToken: this.props.idToken,
                },
                null
            ),

          ]
      );
    } else {
      return h(LoadingIndicator, null, null);
    }
  }
}

export default ReportCardOverview;
